import ProtectedRouter from "@/context/auth/ProtectedRouter";
import { TenantProvider } from "@/context/TenantContext";
import { useTenant } from "@/context/useTenant";
import { getEnvTenantId } from "@/lib/storeManager";
import MainLayout from "@/layouts/MainLayout";
import StarterLayout from "@/layouts/StarterLayout";
import NotFoundPage from "@/pages/NotFoundPage";
import { CommunityManagementRoutes } from "@/project/Community_Management/App";
import routes from "@/utils/routes";
import {
  BrowserRouter,
  Navigate,
  Outlet,
  Route,
  Routes,
} from "react-router-dom";

function App() {
  return (
    <TenantProvider>
      <AppWithRouter />
    </TenantProvider>
  );
}

const AppWithRouter = () => {
  const { tenantId } = useTenant();

  // If no tenant ID is available, show a tenant selection page
  if (!tenantId) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="text-center">
          <h1 className="mb-4 text-2xl font-bold">Tenant ID Required</h1>
          <p className="text-gray-600">
            Set VITE_X_TENANTID in your environment file, or open a URL whose
            first path segment is your tenant ID (e.g., /your-tenant-id/login).
          </p>
        </div>
      </div>
    );
  }
  const publicRoutes = routes.find(m => m.type === "public")?.routes ?? [];
  const authRoutes = routes.find(m => m.type === "auth")?.routes ?? [];
  const filteredPublicRoutes = publicRoutes.filter(
    ({ path }) =>
      path !== "/login" &&
      path !== "/signup" &&
      path !== "/adminlogin" &&
      path !== "/memberlogin"
  );
  return (
    <BrowserRouter basename={getEnvTenantId() ? "/" : `/${tenantId}`}>
      <Routes>
        <Route element={<TopLevelComponent />}>
          <Route
            path="admin/*"
            element={<CommunityManagementRoutes entry="admin" />}
          />
          <Route
            path="member/*"
            element={<CommunityManagementRoutes entry="member" />}
          />
          <Route
            path="adminlogin"
            element={<CommunityManagementRoutes entry="login" />}
          />
          <Route
            path="memberlogin"
            element={<CommunityManagementRoutes entry="member-login" />}
          />
          <Route path="login" element={<Navigate to="/adminlogin" replace />} />
          <Route
            path="login/adminlogin"
            element={<Navigate to="/adminlogin" replace />}
          />
          <Route
            path="signup"
            element={<CommunityManagementRoutes entry="signup" />}
          />

          {filteredPublicRoutes.map(({ path, element: Page }) => (
            <Route key={path} path={path} element={<StarterLayout />}>
              <Route index element={<Page />} />
            </Route>
          ))}

          {/* Protected routes */}
          <Route element={<ProtectedRouter />}>
            <Route element={<MainLayout />}>
              {authRoutes.map(({ path, element: Page }) => (
                <Route key={path} path={path} element={<Page />} />
              ))}
              <Route path="*" element={<NotFoundPage />} />
            </Route>
          </Route>
        </Route>
      </Routes>
    </BrowserRouter>
  );
};

const TopLevelComponent = () => {
  return <Outlet />;
};

export default App;
